import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "flowbite-react";
import { Button } from "@components";
import { HiCheck, HiX, HiExclamationCircle } from "react-icons/hi";
import { panelSm } from "@design-system";

interface FloatingRouteActionsProps {
  hasChanges: boolean;
  hasValidationErrors: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

export const FloatingRouteActions: FC<FloatingRouteActionsProps> = ({
  hasChanges,
  hasValidationErrors,
  isSaving,
  onSave,
  onDiscard
}) => {
  const { t } = useTranslation(['planner', 'common']);

  if (!hasChanges) {
    return null;
  }

  return (
    <div className="absolute top-6 right-6 z-20">
      <div className={`${panelSm} shadow-lg p-4 min-w-[320px] max-w-[400px]`}>
        {/* Header with icon and message */}
        <div className="flex items-center gap-3 mb-4">
          {hasValidationErrors ? (
            <div className="flex-shrink-0 w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <HiExclamationCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
          ) : (
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <HiCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-sm font-medium text-text">
              {hasValidationErrors 
                ? t('planner:route_planner.validation_errors_title', 'Route has validation errors')
                : t('planner:route_planner.unsaved_changes_title', 'You have unsaved route changes')
              }
            </h3>
            <p className="text-xs text-muted mt-1">
              {hasValidationErrors 
                ? t('planner:route_planner.validation_errors_message', 'Fix the errors before saving the route')
                : t('planner:route_planner.unsaved_changes_message', 'Save your changes or discard them to continue')
              }
            </p>
          </div>
        </div>

        {/* Action buttons - back to bottom layout for compact design */}
        <div className="flex gap-3">
          <Button
            color="gray"
            size="sm"
            onClick={onDiscard}
            disabled={isSaving}
            className="flex-1"
          >
            <HiX className="w-4 h-4 mr-2" />
            {t('common:cancel', 'Cancel')}
          </Button>
          <Button
            color={hasValidationErrors ? "gray" : "success"}
            size="sm"
            onClick={onSave}
            disabled={hasValidationErrors || isSaving}
            className="flex-1"
          >
            {isSaving ? (
              <Spinner size="sm" className="mr-2" />
            ) : (
              <HiCheck className="w-4 h-4 mr-2" />
            )}
            {isSaving 
              ? t('planner:saving', 'Saving...') 
              : t('planner:route_planner.save_route', 'Save route')
            }
          </Button>
        </div>
      </div>
    </div>
  );
}; 